<template>
  <div class="about">

    <!-- Company Introduction -->
    <section class="company-intro py-8">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-6 mb-5 reveal-left">
            <div class="intro-image-wrapper">
              <img src="https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/cefc037d5001834810594443328/JE36Y0C1bvQA.png"
                   class="img-fluid rounded shadow-lg"
                   alt="公司厂房">
              <div class="experience-badge">
                <span class="badge-number">20+</span>
                <span class="badge-text">{{ $t('about.experience') }}</span>
              </div>
            </div>
          </div>
          <div class="col-lg-6 reveal-right">
            <div class="intro-content">
              <span class="section-tag">
                <i class="fas fa-building mr-2"></i>{{ $t('about.introTag') }}
              </span>
              <h2 class="section-title text-left mb-4">{{ $t('about.introTitle') }}</h2>
              <p class="mb-4">
                {{ $t('about.introDesc') }}
              </p>
              <p class="mb-5">
                {{ $t('about.introDesc2') }}
              </p>
              <div class="intro-features">
                <div class="feature-item" v-for="(feature, index) in companyFeatures" :key="index">
                  <div class="feature-icon">
                    <i :class="feature.icon"></i>
                  </div>
                  <div class="feature-content">
                    <h5>{{ $t(feature.titleKey) }}</h5>
                    <p>{{ $t(feature.descKey) }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Animated Stats -->
    <section class="company-stats py-8 bg-light">
      <div class="container">
        <div class="section-header text-center reveal">
          <span class="section-tag">
            <i class="fas fa-chart-line mr-2"></i>{{ $t('about.stats.title') }}
          </span>
          <h2 class="section-title">{{ $t('about.stats.title') }}</h2>
        </div>

        <div class="stats-grid">
          <div class="stat-card reveal" v-for="(stat, index) in stats" :key="index" :style="{ transitionDelay: (index * 0.15) + 's' }">
            <div class="stat-icon">
              <i :class="stat.icon"></i>
            </div>
            <div class="stat-value">
              <span class="counter" :data-target="stat.value">{{ stat.displayValue }}</span>
              <span class="stat-unit">{{ stat.unit }}</span>
            </div>
            <div class="stat-label">{{ $t(stat.labelKey) }}</div>
            <div class="stat-progress">
              <div class="progress-bar" :style="{ width: stat.progress + '%' }"></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Company Advantages -->
    <section class="company-advantages py-8">
      <div class="container">
        <div class="section-header text-center reveal">
          <span class="section-tag">
            <i class="fas fa-trophy mr-2"></i>{{ $t('about.advantages.title') }}
          </span>
          <h2 class="section-title">{{ $t('about.advantages.title') }}</h2>
          <p class="section-subtitle">{{ $t('about.advantages.subtitle') }}</p>
        </div>

        <div class="advantages-grid">
          <div class="advantage-card reveal" v-for="(advantage, index) in advantages" :key="index" :style="{ transitionDelay: (index * 0.15) + 's' }">
            <div class="advantage-icon">
              <i :class="advantage.icon"></i>
            </div>
            <div class="advantage-number">{{ advantage.number }}</div>
            <h3 class="advantage-title">{{ $t(advantage.titleKey) }}</h3>
            <p class="advantage-desc">{{ $t(advantage.descKey) }}</p>
            <div class="advantage-features">
              <span v-for="(featureKey, i) in advantage.features" :key="i" class="feature-tag">
                <i class="fas fa-check mr-1"></i>{{ $t(featureKey) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Factory Display -->
    <section class="factory-display py-8 bg-light">
      <div class="container">
        <div class="section-header text-center reveal">
          <span class="section-tag">
            <i class="fas fa-camera mr-2"></i>{{ $t('about.factory.title') }}
          </span>
          <h2 class="section-title">{{ $t('about.factory.title') }}</h2>
          <p class="section-subtitle">{{ $t('about.factory.subtitle') }}</p>
        </div>

        <div class="factory-gallery">
          <div class="gallery-item"
               v-for="(image, index) in factoryImages"
               :key="index"
               :class="'reveal stagger-' + ((index % 4) + 1)"
               :style="{ transitionDelay: ((index % 4) * 0.1) + 's' }">
            <img :src="image" class="img-fluid" :alt="'工厂图片' + (index + 1)">
            <div class="gallery-overlay">
              <div class="gallery-content">
                <i class="fas fa-search-plus fa-2x"></i>
                <span>{{ $t('about.factory.viewLarge') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Timeline Section -->
    <section class="timeline-section py-8">
      <div class="container">
        <div class="section-header text-center reveal">
          <span class="section-tag">
            <i class="fas fa-history mr-2"></i>{{ $t('about.timeline.title') }}
          </span>
          <h2 class="section-title">{{ $t('about.timeline.subtitle') }}</h2>
        </div>

        <div class="timeline">
          <div class="timeline-item reveal" v-for="(item, index) in timeline" :key="index" :class="{ 'reverse': index % 2 === 1 }">
            <div class="timeline-content">
              <span class="timeline-year">{{ item.year }}</span>
              <h4 class="timeline-title">{{ $t(item.titleKey) }}</h4>
              <p class="timeline-desc">{{ $t(item.descKey) }}</p>
            </div>
            <div class="timeline-marker">
              <div class="marker-dot"></div>
              <div class="marker-line"></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section py-8">
      <div class="container">
        <div class="cta-wrapper reveal-scale">
          <div class="cta-content">
            <h2>{{ $t('about.cta.title') }}</h2>
            <p>{{ $t('about.cta.text') }}</p>
            <div class="cta-buttons">
              <a href="/contact" class="btn btn-light btn-lg">
                <i class="fas fa-paper-plane mr-2"></i>{{ $t('nav.contact') }}
              </a>
              <a href="/products" class="btn btn-outline-light btn-lg">
                <i class="fas fa-box mr-2"></i>{{ $t('nav.products') }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'About',
  data() {
    return {
      parallaxOffset: 0,
      pageLoaded: false,
      statsAnimated: false,
      stats: [
        { icon: 'fas fa-calendar-alt', value: 2002, displayValue: 2002, unit: '', labelKey: 'about.stats.founded', progress: 80 },
        { icon: 'fas fa-users', value: 215, displayValue: 0, unit: '+', labelKey: 'about.stats.employees', progress: 70 },
        { icon: 'fas fa-expand-arrows-alt', value: 20000, displayValue: 0, unit: 'm²', labelKey: 'about.stats.area', progress: 85 },
        { icon: 'fas fa-box', value: 5000, displayValue: 0, unit: '+', labelKey: 'about.stats.exports', progress: 75 }
      ],
      companyFeatures: [
        { icon: 'fas fa-industry', titleKey: 'about.features.factory.title', descKey: 'about.features.factory.desc' },
        { icon: 'fas fa-cogs', titleKey: 'about.features.equipment.title', descKey: 'about.features.equipment.desc' },
        { icon: 'fas fa-warehouse', titleKey: 'about.features.storage.title', descKey: 'about.features.storage.desc' }
      ],
      advantages: [
        {
          icon: 'fas fa-industry',
          number: '01',
          titleKey: 'about.advantages.direct.title',
          descKey: 'about.advantages.direct.desc',
          features: ['about.advantages.direct.features.1', 'about.advantages.direct.features.2', 'about.advantages.direct.features.3']
        },
        {
          icon: 'fas fa-palette',
          number: '02',
          titleKey: 'about.advantages.custom.title',
          descKey: 'about.advantages.custom.desc',
          features: ['about.advantages.custom.features.1', 'about.advantages.custom.features.2', 'about.advantages.custom.features.3']
        },
        {
          icon: 'fas fa-award',
          number: '03',
          titleKey: 'about.advantages.quality.title',
          descKey: 'about.advantages.quality.desc',
          features: ['about.advantages.quality.features.1', 'about.advantages.quality.features.2', 'about.advantages.quality.features.3']
        },
        {
          icon: 'fas fa-truck',
          number: '04',
          titleKey: 'about.advantages.delivery.title',
          descKey: 'about.advantages.delivery.desc',
          features: ['about.advantages.delivery.features.1', 'about.advantages.delivery.features.2', 'about.advantages.delivery.features.3']
        }
      ],
      factoryImages: [
        'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/175e7b175001834810618501627/IIH76CDaflYA.jpg',
        'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/cd20b6f45001834810600976131/qAtJCfSK4xkA.jpg',
        'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/0e75c58f5001834810618160015/fIP8qnIi5coA.jpg',
        'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/0e75c6595001834810618160079/jgCakz09dTwA.jpg',
        'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/cd20b7805001834810600976179/wc6qaavvTLYA.jpg',
        'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/491576be5001834810599172343/VsAivopRDvEA.jpg',
        'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/2feeeebd5001834810608728272/07iYGVAbm24A.jpg',
        'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/e56d90065001834810607847658/0VTv2XLFk6QA.jpg'
      ],
      timeline: [
        { year: '1', titleKey: 'about.timeline.founded', descKey: 'about.timeline.foundedDesc' },
        { year: '2', titleKey: 'about.timeline.expansion', descKey: 'about.timeline.expansionDesc' },
        { year: '3', titleKey: 'about.timeline.innovation', descKey: 'about.timeline.innovationDesc' },
        { year: '4', titleKey: 'about.timeline.market', descKey: 'about.timeline.marketDesc' },
        { year: '5', titleKey: 'about.timeline.modernization', descKey: 'about.timeline.modernizationDesc' },
        { year: '6', titleKey: 'about.timeline.development', descKey: 'about.timeline.developmentDesc' }
      ]
    }
  },
  mounted() {
    setTimeout(() => {
      this.pageLoaded = true
    }, 200)

    window.addEventListener('scroll', this.handleScroll)
    this.initRevealAnimations()
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    handleScroll() {
      this.parallaxOffset = window.scrollY * 0.3
    },
    initRevealAnimations() {
      const reveals = document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale')

      const revealOnScroll = () => {
        reveals.forEach(element => {
          const windowHeight = window.innerHeight
          const elementTop = element.getBoundingClientRect().top
          const revealPoint = 100

          if (elementTop < windowHeight - revealPoint) {
            element.classList.add('active')
          }
        })

        // Animate stats
        const statsSection = document.querySelector('.stats-grid')
        if (statsSection && !this.statsAnimated) {
          const statsTop = statsSection.getBoundingClientRect().top
          if (statsTop < window.innerHeight - 100) {
            this.statsAnimated = true
            this.animateCounters()
          }
        }
      }

      window.addEventListener('scroll', revealOnScroll)
      window.addEventListener('load', revealOnScroll)

      revealOnScroll()
    },
    animateCounters() {
      this.stats.forEach((stat, index) => {
        if (stat.value === 2002) {
          stat.displayValue = stat.value
          return
        }

        const duration = 2000
        const startTime = performance.now()
        const target = stat.value

        const animate = (currentTime) => {
          const elapsed = currentTime - startTime
          const progress = Math.min(elapsed / duration, 1)
          const easeOutQuart = 1 - Math.pow(1 - progress, 4)
          stat.displayValue = Math.floor(easeOutQuart * target)

          if (progress < 1) {
            requestAnimationFrame(animate)
          }
        }

        setTimeout(() => {
          requestAnimationFrame(animate)
        }, index * 200)
      })
    }
  }
}
</script>

<style scoped>
.about {
  padding-top: 0;
}

/* Page Header */
.page-header {
  position: relative;
  height: 400px;
  overflow: hidden;
  display: flex;
  align-items: center;
}

.page-header-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 120%;
  background: linear-gradient(135deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.5) 100%),
              url('https://trae-api-cn.mchost.guru/api/ide/v1/text_to_image?prompt=Industrial%20safety%20equipment%20welding%20protection%20background&image_size=landscape_16_9');
  background-size: cover;
  background-position: center;
  will-change: transform;
}

.page-header-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.4) 100%);
}

.page-header-content {
  position: relative;
  z-index: 2;
  width: 100%;
}

.page-header-text {
  text-align: center;
  color: white;
}

.page-title {
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 15px;
}

.page-title span {
  display: inline-block;
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.page-title.animate-in span {
  opacity: 1;
  transform: translateY(0);
}

.page-subtitle {
  font-size: 1.2rem;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.8s ease;
  transition-delay: 0.3s;
}

.page-subtitle.animate-in {
  opacity: 0.9;
  transform: translateY(0);
}

/* Section Styles */
.section-tag {
  display: inline-block;
  padding: 8px 20px;
  background: linear-gradient(135deg, #fff5f5, #ffebee);
  color: #e53935;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 15px;
}

.section-tag-light {
  background: linear-gradient(135deg, rgba(255,255,255,0.15), rgba(255,255,255,0.1));
  color: white;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 15px;
}

.section-title-light {
  color: white;
}

.section-subtitle {
  font-size: 1.1rem;
  color: #7f8c8d;
  margin-bottom: 50px;
}

.section-subtitle-light {
  color: rgba(255,255,255,0.8);
}

/* Intro Section */
.intro-image-wrapper {
  position: relative;
}

.experience-badge {
  position: absolute;
  bottom: -20px;
  right: -20px;
  background: linear-gradient(135deg, #e53935, #c62828);
  color: white;
  padding: 25px 30px;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 15px 40px rgba(229, 57, 53, 0.4);
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

.badge-number {
  display: block;
  font-size: 2.5rem;
  font-weight: 800;
  line-height: 1;
}

.badge-text {
  font-size: 0.9rem;
  opacity: 0.9;
}

.intro-features {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 20px;
  background: #fff5f5;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.feature-item:hover {
  transform: translateX(10px);
  box-shadow: 0 10px 30px rgba(229, 57, 53, 0.1);
}

.feature-icon {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #e53935, #c62828);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.feature-content h5 {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 5px;
}

.feature-content p {
  font-size: 0.9rem;
  color: #666;
  margin: 0;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
}

.stat-card {
  background: white;
  padding: 35px 25px;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0,0,0,0.05);
  border: 1px solid #f0f0f0;
  transition: all 0.4s ease;
}

.stat-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 50px rgba(229, 57, 53, 0.1);
}

.stat-icon {
  width: 60px;
  height: 60px;
  margin: 0 auto 20px;
  background: linear-gradient(135deg, #fff5f5, #ffebee);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #e53935;
}

.stat-value {
  font-size: 2.8rem;
  font-weight: 800;
  color: #e53935;
  line-height: 1;
  margin-bottom: 10px;
}

.stat-unit {
  font-size: 1.5rem;
}

.stat-label {
  font-size: 1rem;
  color: #7f8c8d;
  margin-bottom: 20px;
}

.stat-progress {
  height: 6px;
  background: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #e53935, #ff6f00);
  border-radius: 3px;
  transition: width 1.5s ease;
}

/* Advantages Grid */
.advantages-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
}

.advantage-card {
  background: white;
  padding: 40px 30px;
  border-radius: 24px;
  position: relative;
  overflow: hidden;
  border: 1px solid #f0f0f0;
  transition: all 0.4s ease;
}

.advantage-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 25px 60px rgba(0,0,0,0.1);
  border-color: #e53935;
}

.advantage-icon {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #e53935, #c62828);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: white;
  margin-bottom: 25px;
  box-shadow: 0 10px 30px rgba(229, 57, 53, 0.3);
}

.advantage-number {
  position: absolute;
  top: 20px;
  right: 25px;
  font-size: 4rem;
  font-weight: 800;
  color: #e53935;
  opacity: 0.1;
}

.advantage-title {
  font-size: 1.3rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
}

.advantage-desc {
  font-size: 0.95rem;
  color: #666;
  line-height: 1.7;
  margin-bottom: 20px;
}

.advantage-features {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.feature-tag {
  font-size: 0.85rem;
  padding: 6px 14px;
  background: #fff5f5;
  color: #e53935;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
}

/* Factory Gallery */
.factory-gallery {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 25px;
}

.gallery-item {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  aspect-ratio: 1;
}

.gallery-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.gallery-item:hover img {
  transform: scale(1.15);
}

.gallery-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(229, 57, 53, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.gallery-item:hover .gallery-overlay {
  opacity: 1;
}

.gallery-content {
  text-align: center;
  color: white;
}

.gallery-content i {
  margin-bottom: 10px;
}

.gallery-content span {
  display: block;
  font-size: 0.9rem;
}

/* Timeline */
.timeline-section {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
}

.timeline {
  position: relative;
  max-width: 900px;
  margin: 0 auto;
}

.timeline::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 3px;
  height: 100%;
  background: linear-gradient(180deg, #e53935, #ff6f00);
}

.timeline-item {
  display: flex;
  justify-content: flex-end;
  padding-right: 50%;
  position: relative;
  margin-bottom: 50px;
}

.timeline-item.reverse {
  justify-content: flex-start;
  padding-right: 0;
  padding-left: 50%;
}

.timeline-content {
  background: rgba(255,255,255,0.1);
  padding: 25px 30px;
  border-radius: 16px;
  max-width: 380px;
  border: 1px solid rgba(255,255,255,0.1);
}

.timeline-year {
  display: inline-block;
  padding: 5px 15px;
  background: #e53935;
  color: white;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 15px;
}

.timeline-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: white;
  margin-bottom: 10px;
}

.timeline-desc {
  font-size: 0.95rem;
  color: rgba(255,255,255,0.7);
  margin: 0;
}

.timeline-marker {
  position: absolute;
  left: 50%;
  top: 0;
  transform: translateX(-50%);
}

.marker-dot {
  width: 20px;
  height: 20px;
  background: #e53935;
  border-radius: 50%;
  border: 4px solid #fff;
  box-shadow: 0 0 20px rgba(229, 57, 53, 0.5);
}

.marker-line {
  width: 2px;
  height: 100%;
  background: linear-gradient(180deg, #e53935, transparent);
  margin-top: 10px;
}

/* CTA Section */
.cta-wrapper {
  background: linear-gradient(135deg, #e53935, #c62828);
  border-radius: 24px;
  padding: 60px;
  text-align: center;
  color: white;
}

.cta-content h2 {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 15px;
}

.cta-content p {
  font-size: 1.1rem;
  opacity: 0.9;
  margin-bottom: 30px;
}

.cta-buttons {
  display: flex;
  gap: 20px;
  justify-content: center;
}

.cta-buttons .btn-light {
  background: white;
  color: #e53935;
  border: none;
}

.cta-buttons .btn-light:hover {
  background: #f5f5f5;
}

.cta-buttons .btn-outline-light {
  border: 2px solid white;
  color: white;
  background: transparent;
}

.cta-buttons .btn-outline-light:hover {
  background: white;
  color: #e53935;
}

/* Utilities */
.py-8 {
  padding-top: 100px;
  padding-bottom: 100px;
}

.text-left {
  text-align: left;
}

/* Reveal Animations */
.reveal, .reveal-left, .reveal-right, .reveal-scale {
  opacity: 0;
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.reveal {
  transform: translateY(30px);
}

.reveal-left {
  transform: translateX(-50px);
}

.reveal-right {
  transform: translateX(50px);
}

.reveal-scale {
  transform: scale(0.9);
}

.reveal.active, .reveal-left.active, .reveal-right.active, .reveal-scale.active {
  opacity: 1;
  transform: translate(0) scale(1);
}

/* Responsive */
@media (max-width: 991px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .advantages-grid {
    grid-template-columns: 1fr;
  }

  .factory-gallery {
    grid-template-columns: repeat(2, 1fr);
  }

  .timeline::before {
    left: 20px;
  }

  .timeline-item, .timeline-item.reverse {
    padding-left: 60px;
    padding-right: 0;
    justify-content: flex-start;
  }

  .timeline-marker {
    left: 20px;
  }
}

@media (max-width: 767px) {
  .page-title {
    font-size: 2.5rem;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .factory-gallery {
    grid-template-columns: 1fr;
  }

  .cta-buttons {
    flex-direction: column;
  }

  .cta-wrapper {
    padding: 40px 20px;
  }

  .experience-badge {
    position: static;
    margin-top: 20px;
    display: inline-block;
  }
}
</style>
